import {
  CentraCheckoutApiProduct,
  CentraCheckoutApiProductItem,
} from "@made-people/centra-checkout-api";
import { ProductPurchaseInfo } from "@made-people/grease-product";
import {
  CentraCheckoutApiMapperOptions,
  CentraCheckoutApiMappers,
} from "@made-people/grease-centra";
import { getTableMappingIdForCountryCode } from "./utils/get-table-mapping-id-for-country-code";
import {
  isWeeksStatus,
  parseLitWarehouseStatus,
} from "./utils/parse-lit-warehouse-status";

function getLitWarehouseStatusForItem(
  product: CentraCheckoutApiProduct,
  item: CentraCheckoutApiProductItem,
  options?: CentraCheckoutApiMapperOptions,
): string | null {
  const litWarehouseStatusRaw = product.out_of_stock_message_lit_text;

  if (typeof litWarehouseStatusRaw !== "string" || !options) {
    return null;
  }

  const tableMappingId = getTableMappingIdForCountryCode(
    options.context.countryCode,
  );
  const tableMapping = product.tableMappings?.[tableMappingId];

  if (!tableMapping) {
    return null;
  }

  return parseLitWarehouseStatus(
    litWarehouseStatusRaw,
    item.name,
    tableMapping,
  );
}

export const mmPurchaseInfo: CentraCheckoutApiMappers["centraCheckoutApiItemToGreasePurchaseInfo"] =
  (
    product: CentraCheckoutApiProduct,
    item: CentraCheckoutApiProductItem,
    options?: CentraCheckoutApiMapperOptions,
  ): ProductPurchaseInfo => {
    const isDiscounted =
      product.priceAsNumber !== product.priceBeforeDiscountAsNumber;
    const discountPercent = Math.round(
      (1 - product.priceAsNumber / product.priceBeforeDiscountAsNumber) * 100,
    );

    const quantity =
      typeof item.stock === "number"
        ? item.stock
        : item.stock === "yes"
          ? 1
          : 0;

    const litStatus = getLitWarehouseStatusForItem(product, item, options);
    const hasWeeksStatus = isWeeksStatus(litStatus);
    const inStock = quantity > 0 || hasWeeksStatus;

    return {
      sku: item.sku,
      inventory: {
        quantity, // Quantity is the stock quantity of the item, do not take into account the weeks status
        inStock, // InStock is true if the quantity is greater than 0 or if the litStatus is a weeks status
        warehouseId: "1",
      },
      additionalInventory: [],
      priceInfo: {
        price: { formatted: product.price, number: product.priceAsNumber },
        priceWithoutDiscount: {
          formatted: product.priceBeforeDiscount,
          number: product.priceBeforeDiscountAsNumber,
        },

        previouslyDiscountedPrices: product.lowestPrice?.map((lp) => ({
          date: new Date(Date.now() - lp.periodDays * 24 * 60 * 60 * 1000),
          periodDays: lp.periodDays,
          price: { formatted: lp.price, number: lp.priceAsNumber },
          priceWithoutDiscount: {
            formatted: lp.priceBeforeDiscount,
            number: lp.priceBeforeDiscountAsNumber,
          },
        })),
        discountInfo: isDiscounted
          ? {
              percent: discountPercent,
            }
          : undefined,
      },
    };
  };
